import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSessionStore, useUIStore } from '@/stores'
import { authService } from '@/services/authService'
import { setOrderDetail, formatErrorMessage } from '@/utils'
import { getSessionStepInfo } from '@/utils/session'
import { emitProcessingMessages, clearProcessingMessages } from '@/utils/processing'
import { useToast } from '@/hooks/useToast'
import {
  inputTypeForPath,
  parentStepType,
  pathForStepType,
  findPortalInput,
  isItemScopedPath,
} from '../utils/portalNav'
import { isSyncedToUrl, resyncPortalSession } from '../utils/portalRecovery'
import { isDrawerOwningFunnel } from '../utils/portalSession'
import { HTTP_STATUS } from '@/constants'
import { isPreviewMode } from '@/utils/previewMode'

// Restores the funnel's navigation semantics for OMP: entering a portal page
// fires POST /sessions/{id}/input/ (showing the processing GIF), and back fires
// POST /sessions/{id}/undo/. Falls back to plain client navigation whenever the
// session can't drive it (no matching input, same step, no session).
export function useOmpNav() {
  const navigate = useNavigate()
  const toast = useToast()

  const navigateWithInput = useCallback(
    async (to: string, opts?: { orderId?: number | string }) => {
      // The preview's session is a fixture with no server behind it, so every
      // funnel step here would fail. Move the router and nothing else.
      if (isPreviewMode()) {
        navigate(to, { viewTransition: true })
        return
      }

      const ui = useUIStore.getState()
      const targetType = inputTypeForPath(to)
      const sessionId = (useSessionStore.getState().session as any)?.id

      // Routes the funnel doesn't model — client navigation is all they need.
      if (!sessionId || targetType === undefined) {
        navigate(to, { viewTransition: true })
        return
      }

      // A funnel step in state.session means the server is behind the URL. Never
      // move without it: resync first, and give up rather than fake the step.
      if (!isDrawerOwningFunnel() && !isSyncedToUrl() && !(await resyncPortalSession())) {
        toast.error('We could not open that page. Please try again.')
        return
      }

      // Compared with the query string: dropping a filter (?subscription=…) keeps the
      // pathname but is still a navigation, and must not be swallowed here.
      if (to === window.location.pathname + window.location.search) {
        return
      }

      const session = useSessionStore.getState().session as any
      // The server is already on this step and the route isn't item-scoped, so there
      // is nothing to submit — only the URL needs to move.
      if (Number(session?.step_type) === targetType && !isItemScopedPath(to)) {
        navigate(to, { viewTransition: true })
        return
      }

      const parentType = parentStepType(targetType)
      const direct = findPortalInput(session, targetType)
      const viaParent = direct ? undefined : parentType && findPortalInput(session, parentType)
      if (!direct && !viaParent) {
        toast.error('We could not open that page. Please try again.')
        return
      }

      try {
        ui.setLoading(true)
        emitProcessingMessages(null, (direct ?? viaParent).action_results ?? null)
        if (opts?.orderId != null) {
          setOrderDetail(String(opts.orderId))
        }
        let input = direct
        if (!input) {
          const parentStep = await authService.input(sessionId, { input: viaParent.id })
          useSessionStore.getState().setSession(parentStep)
          input = findPortalInput(parentStep, targetType)
          if (!input) {
            toast.error('We could not open that page. Please try again.')
            return
          }
        }
        const result = await authService.input(sessionId, { input: input.id })
        const store = useSessionStore.getState()
        store.setSession(result)
        if (result?.can_undo) {
          store.addToStepStack(getSessionStepInfo(result))
        }
        navigate(to, { viewTransition: true })
      } catch (err) {
        toast.error(formatErrorMessage(err))
        await resyncPortalSession()
      } finally {
        clearProcessingMessages()
        ui.setLoading(false)
      }
    },
    [navigate, toast]
  )

  const goBack = useCallback(
    async (fallbackTo?: string) => {
      if (isPreviewMode()) {
        if (fallbackTo) {
          navigate(fallbackTo, { viewTransition: true })
        } else {
          navigate(-1)
        }
        return
      }

      if (!isDrawerOwningFunnel() && !isSyncedToUrl()) {
        await resyncPortalSession()
      }

      const store = useSessionStore.getState()
      const session = store.session as any
      if (!session?.can_undo) {
        if (fallbackTo) {
          navigate(fallbackTo, { viewTransition: true })
        } else {
          navigate(-1)
        }
        return
      }
      try {
        const result = await store.undo(1)
        // Prefer the caller's known parent route (back links pass it); only fall
        // back to deriving it from the restored step when none was given.
        const route = fallbackTo ?? pathForStepType((result as any)?.step_type)
        if (route) {
          navigate(route, { replace: true, viewTransition: true })
        } else {
          navigate(-1)
        }
      } catch (err: any) {
        if (err?.response?.status !== HTTP_STATUS.UNPROCESSABLE_ENTITY) {
          toast.error(formatErrorMessage(err))
        }
        await resyncPortalSession()
        if (fallbackTo) {
          navigate(fallbackTo, { replace: true })
        }
      }
    },
    [navigate, toast]
  )

  return { navigateWithInput, goBack }
}
